<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import type { EChartsOption } from 'echarts'
import { usePrototypeStore } from '@/stores/prototype'
import { useAuthStore } from '@/stores/auth'
import EChart from '@/components/ui/EChart.vue'
import AppIcon from '@/components/AppIcon.vue'
import ProDateRange from '@/components/pro/ProDateRange.vue'
import { formatDate, formatDateTime, formatNumber } from '@/utils/format'

const store = usePrototypeStore()
const auth = useAuthStore()
const router = useRouter()
const trendMetric = ref<'active' | 'new' | 'adherence'>('active')
const rangeMode = ref<'7' | '30' | 'custom'>('7')
const customRange = ref<[string, string] | null>(['2026-10-01', '2026-10-17'])

const db = store.db
const today = '2026-10-17'
const todayLabel = '2026年10月17日 星期六'
const greetingName = computed(() => auth.profile?.name ?? '管理员')

function keyOf(value: unknown): string {
  return String(value ?? '').slice(0, 10)
}

function percent(part: number, total: number): number {
  return total ? Number(((part / total) * 100).toFixed(1)) : 0
}

function countBy(rows: Array<Record<string, unknown>>, field: string): Record<string, number> {
  const result: Record<string, number> = {}
  for (const row of rows) result[String(row[field] ?? '未知')] = (result[String(row[field] ?? '未知')] ?? 0) + 1
  return result
}

const users = computed(() => db.users)
const families = computed(() => db.families)
const records = computed(() => db.medication_records)
const plans = computed(() => db.medication_plans)
const alerts = computed(() => db.alerts)
const devices = computed(() => db.devices)

const activeUsers = computed(() => users.value.filter(user => user.status === 'active').length)
const pendingReminders = computed(() => plans.value.filter(plan => plan.status === 'active' || plan.status === 'overdue').length)
const openAlerts = computed(() => alerts.value.filter(alert => ['pending', 'processing'].includes(String(alert.status))))
const familyMembers = computed(() => families.value.reduce((total, family) => total + Number(family.member_count ?? 0), 0))
const boundDevices = computed(() => devices.value.filter(device => Boolean(device.user_id) && device.status !== 'unbound').length)
const takenRecords = computed(() => records.value.filter(record => record.status === 'taken').length)
const adherence = computed(() => percent(takenRecords.value, records.value.length))

function miniBars(seed: number[]): number[] {
  return seed
}

const kpis = computed(() => [
  {
    key: 'active',
    label: '在册活跃用户',
    value: formatNumber(activeUsers.value),
    icon: 'users',
    tone: 'brand',
    foot: `其中 60 岁以上 ${formatNumber(users.value.filter(user => Number(user.age ?? 0) >= 60).length)} 人`,
    bars: miniBars([36, 52, 44, 66, 58, 76, 69, 84, 78, 93]),
  },
  {
    key: 'plans',
    label: '待提醒用药',
    value: formatNumber(pendingReminders.value),
    icon: 'pill',
    tone: 'info',
    foot: `今日计划 ${formatNumber(plans.value.length)} 条`,
    bars: miniBars([70, 52, 64, 46, 61, 55, 72, 50, 65, 58]),
  },
  {
    key: 'alerts',
    label: '异常健康预警',
    value: formatNumber(openAlerts.value.length),
    icon: 'alert',
    tone: 'danger',
    foot: `高风险 ${formatNumber(openAlerts.value.filter(alert => alert.severity === '高').length)} 条待处理`,
    bars: miniBars([30, 44, 28, 39, 54, 34, 42, 48, 35, 31]),
  },
  {
    key: 'family',
    label: '家庭成员总数',
    value: formatNumber(familyMembers.value),
    icon: 'family',
    tone: 'purple',
    foot: `${formatNumber(families.value.length)} 个家庭档案`,
    bars: miniBars([38, 45, 50, 48, 58, 61, 66, 65, 72, 79]),
  },
  {
    key: 'devices',
    label: '设备连接数',
    value: formatNumber(boundDevices.value),
    icon: 'device',
    tone: 'cyan',
    foot: `在线 ${formatNumber(devices.value.filter(device => device.status === 'online').length)} 台`,
    bars: miniBars([46, 49, 45, 55, 51, 62, 58, 64, 61, 70]),
  },
  {
    key: 'adherence',
    label: '用药完成率',
    value: `${adherence.value.toFixed(1)}%`,
    icon: 'check-circle',
    tone: 'success',
    foot: `已服药 ${formatNumber(takenRecords.value)} 次`,
    bars: miniBars([58, 61, 60, 66, 71, 69, 76, 78, 82, 85]),
  },
])

const periodEnd = computed(() => {
  if (rangeMode.value === 'custom' && customRange.value?.[1]) return customRange.value[1]
  return today
})

const periodStart = computed(() => {
  if (rangeMode.value === 'custom' && customRange.value?.[0]) return customRange.value[0]
  const end = new Date(`${periodEnd.value}T12:00:00+08:00`)
  const start = new Date(end.getTime() - (rangeMode.value === '30' ? 29 : 6) * 86_400_000 + 8 * 3_600_000)
  return `${start.getUTCFullYear()}-${String(start.getUTCMonth() + 1).padStart(2, '0')}-${String(start.getUTCDate()).padStart(2, '0')}`
})

const periodDates = computed(() => {
  const end = new Date(`${periodEnd.value}T12:00:00+08:00`)
  const start = new Date(`${periodStart.value}T12:00:00+08:00`)
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || start > end) return [periodEnd.value]
  const days: string[] = []
  for (let cursor = start.getTime(); cursor <= end.getTime() && days.length < 90; cursor += 86_400_000) {
    const shanghai = new Date(cursor + 8 * 3_600_000)
    days.push(`${shanghai.getUTCFullYear()}-${String(shanghai.getUTCMonth() + 1).padStart(2, '0')}-${String(shanghai.getUTCDate()).padStart(2, '0')}`)
  }
  return days.length ? days : [periodEnd.value]
})

const periodDays = computed(() => periodDates.value.length)
const trendLabels = computed(() => periodDates.value.map(day => {
  const [, month, date] = day.split('-')
  return `${Number(month)}/${Number(date)}`
}))

function dayStats(day: string): { active: number; created: number; taken: number; total: number } {
  const active = new Set(users.value.filter(user => keyOf(user.last_active_at) === day).map(user => String(user.id))).size
  const created = users.value.filter(user => keyOf(user.registered_at) === day).length
  const dayRecords = records.value.filter(record => keyOf(record.scheduled_at) === day)
  return {
    active,
    created,
    taken: dayRecords.filter(record => record.status === 'taken').length,
    total: dayRecords.length,
  }
}

const trendData = computed(() => periodDates.value.map(day => {
  const stats = dayStats(day)
  if (trendMetric.value === 'new') return stats.created
  if (trendMetric.value === 'adherence') return Math.round(percent(stats.taken, stats.total))
  return stats.active
}))

const trendOption = computed<EChartsOption>(() => ({
  animationDuration: 520,
  grid: { left: 10, right: 18, top: 18, bottom: 26, containLabel: true },
  tooltip: { trigger: 'axis', backgroundColor: '#fff', borderColor: '#e8eef4', textStyle: { color: '#1f2d45', fontSize: 11 } },
  xAxis: {
    type: 'category',
    boundaryGap: false,
    data: trendLabels.value,
    axisLine: { lineStyle: { color: '#e8eef4' } },
    axisTick: { show: false },
    axisLabel: { color: '#9aa8bd', fontSize: 10, interval: periodDays.value > 10 ? 4 : 0 },
  },
  yAxis: {
    type: 'value',
    splitNumber: 4,
    axisLabel: { color: '#9aa8bd', fontSize: 10 },
    splitLine: { lineStyle: { color: '#f0f4f8', type: 'dashed' } },
  },
  series: [{
    type: 'line',
    smooth: 0.36,
    data: trendData.value,
    symbol: 'circle',
    symbolSize: 5,
    showSymbol: periodDays.value <= 7,
    lineStyle: { color: '#08b578', width: 2.2 },
    itemStyle: { color: '#08b578', borderColor: '#fff', borderWidth: 2 },
    areaStyle: {
      color: {
        type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
        colorStops: [{ offset: 0, color: 'rgba(8,181,120,.22)' }, { offset: 1, color: 'rgba(8,181,120,.015)' }],
      },
    },
  }],
}))

const donutData = computed(() => {
  const counts = countBy(records.value, 'status')
  return [
    { name: '已按时', value: counts.taken ?? 0, itemStyle: { color: '#08b578' } },
    { name: '已逾期', value: counts.overdue ?? 0, itemStyle: { color: '#f04444' } },
    { name: '待确认', value: counts.pending ?? 0, itemStyle: { color: '#f5a623' } },
    { name: '已跳过', value: counts.skipped ?? 0, itemStyle: { color: '#9aa8bd' } },
  ]
})

const donutOption = computed<EChartsOption>(() => ({
  animationDuration: 520,
  tooltip: { trigger: 'item', backgroundColor: '#fff', borderColor: '#e8eef4', textStyle: { color: '#1f2d45', fontSize: 11 } },
  series: [{
    type: 'pie',
    radius: ['62%', '82%'],
    center: ['50%', '48%'],
    avoidLabelOverlap: true,
    label: { show: false },
    itemStyle: { borderColor: '#fff', borderWidth: 3 },
    data: donutData.value,
  }],
  graphic: [
    { type: 'text', left: 'center', top: '39%', style: { text: `${adherence.value.toFixed(0)}%`, fill: '#0b1b3a', fontSize: 24, fontWeight: 700, fontFamily: 'DIN Alternate, sans-serif' } },
    { type: 'text', left: 'center', top: '53%', style: { text: '用药完成率', fill: '#7183a1', fontSize: 10 } },
  ],
}))

const riskAlerts = computed(() => [...openAlerts.value]
  .sort((a, b) => {
    const weight: Record<string, number> = { 高: 3, 中: 2, 低: 1 }
    return (weight[String(b.severity)] ?? 0) - (weight[String(a.severity)] ?? 0)
  })
  .slice(0, 5))

const familyOverview = computed(() => {
  const total = families.value.length
  const members = familyMembers.value
  const solo = families.value.filter(family => family.family_type === '独居长者').length
  const multi = families.value.filter(family => family.family_type === '三代同堂').length
  const elderly = users.value.filter(user => Number(user.age ?? 0) >= 60).length
  return {
    total,
    members,
    average: total ? (members / total).toFixed(1) : '0',
    solo,
    multi,
    elderly,
    soloRate: percent(solo, total),
    multiRate: percent(multi, total),
    others: Math.max(0, members - elderly),
  }
})

const metricCards = computed(() => {
  const types = ['血压', '心率', '血糖']
  const colors = ['danger', 'info', 'success']
  return types.map((type, index) => {
    const typeAlerts = openAlerts.value.filter(alert => String(alert.alert_type).includes(type))
    const abnormal = typeAlerts.length
    const normal = Math.max(0, users.value.length - abnormal)
    return {
      type,
      abnormal,
      normal,
      high: typeAlerts.filter(alert => alert.severity === '高').length,
      tone: colors[index],
      icon: type === '血压' ? 'heart' : type === '心率' ? 'analytics' : 'drop',
    }
  })
})

const todoFilter = ref<'all' | 'high' | 'today'>('all')
const completedTodos = ref<string[]>([])

const todos = computed(() => {
  const high = openAlerts.value.filter(alert => alert.severity === '高').slice(0, 2)
  const offline = devices.value.filter(device => device.status === 'fault' || device.status === 'offline').slice(0, 1)
  const review = db.drugs.filter(drug => drug.review_status === 'pending').slice(0, 1)
  const scheduled = db.messages.filter(message => message.status === 'scheduled').slice(0, 1)
  const rows: Array<{ id: string; title: string; meta: string; tone: string; route: string; at: string }> = []
  for (const alert of high) rows.push({ id: `alert-${alert.id}`, title: `处理 ${alert.user_name} 的${alert.alert_type}预警`, meta: `${alert.metric_value} · ${formatDateTime(alert.triggered_at)}`, tone: 'high', route: '#/health/alerts', at: String(alert.triggered_at ?? '') })
  for (const device of offline) rows.push({ id: `device-${device.id}`, title: `跟进设备 ${device.device_no} 异常`, meta: `${device.user_name || '未绑定'} · ${device.status === 'fault' ? '故障' : '离线'}`, tone: 'warning', route: '#/devices', at: String(device.last_report_at ?? device.updated_at ?? '') })
  for (const drug of review) rows.push({ id: `drug-${drug.id}`, title: `审核药品 ${drug.generic_name}`, meta: `待药事审核 · 更新于 ${formatDateTime(drug.updated_at)}`, tone: 'normal', route: '#/drugs/review', at: String(drug.updated_at ?? '') })
  for (const message of scheduled) rows.push({ id: `message-${message.id}`, title: `确认通知「${message.title}」发送`, meta: `计划 ${formatDateTime(message.scheduled_at)} · ${message.audience_label}`, tone: 'normal', route: '#/messages', at: String(message.scheduled_at ?? '') })
  return rows
})

const visibleTodos = computed(() => todos.value.filter(todo => {
  if (completedTodos.value.includes(todo.id)) return false
  if (todoFilter.value === 'high') return todo.tone === 'high'
  if (todoFilter.value === 'today') return todo.id.startsWith('alert-') || todo.id.startsWith('message-')
  return true
}))

function toggleTodo(id: string): void {
  completedTodos.value = completedTodos.value.includes(id)
    ? completedTodos.value.filter(item => item !== id)
    : [...completedTodos.value, id]
}

const quickActions = [
  { label: '新增用户', sub: '建立个人或家庭档案', icon: 'users', tone: 'brand', route: '#/users?panel=create' },
  { label: '添加药品', sub: '维护平台药品资料', icon: 'drug', tone: 'info', route: '#/drugs' },
  { label: '发送通知', sub: '推送给指定健康人群', icon: 'bell', tone: 'purple', route: '#/messages' },
  { label: '查看数据分析', sub: '多维度经营与健康指标', icon: 'analytics', tone: 'warning', route: '#/analytics' },
  { label: '设备管理', sub: '在线、离线与固件任务', icon: 'device', tone: 'cyan', route: '#/devices' },
  { label: '系统参数', sub: '平台、安全与原型设置', icon: 'settings', tone: 'neutral', route: '#/settings/general' },
]

function go(route: string): void {
  void router.push(route.replace(/^#/, ''))
}

function openAlert(alert: Record<string, unknown>): void {
  void router.push({ path: '/health/alerts', query: { focus: String(alert.user_name ?? '') } })
}

function goUser(userId: unknown): void {
  const page = db.users.find(user => String(user.id) === String(userId))
  if (page) void router.push({ path: '/users', query: { focus: String(page.name) } })
}

function severityTone(value: unknown): string {
  if (value === '高') return 'danger'
  if (value === '中') return 'warning'
  return 'success'
}

function alertIcon(type: unknown): string {
  if (String(type).includes('血压') || String(type).includes('心率')) return 'heart'
  if (String(type).includes('血糖')) return 'drop'
  return 'activity'
}

</script>

<template>
  <div class="dash-page">
    <header class="dash-greeting">
      <div>
        <h1>您好，{{ greetingName }}！</h1>
        <p>今天是 {{ todayLabel }} <span class="dash-weather">晴 18℃</span><span class="dash-divider">|</span>用数据守护更多家庭的健康</p>
      </div>
      <div class="dash-greeting__meta">
        <span class="prototype-pill">原型数据</span>
        <span class="dash-sync"><AppIcon name="refresh" :size="13" /> 内存实时汇总</span>
      </div>
    </header>

    <section class="dash-kpis" aria-label="核心运营指标">
      <button v-for="kpi in kpis" :key="kpi.key" type="button" class="dash-kpi" :class="`dash-kpi--${kpi.tone}`" @click="kpi.key === 'alerts' ? go('#/health/alerts') : kpi.key === 'plans' ? go('#/medication/plans') : kpi.key === 'devices' ? go('#/devices') : go('#/users')">
        <span class="dash-kpi__icon"><AppIcon :name="kpi.icon" :size="20" /></span>
        <span class="dash-kpi__body">
          <span class="dash-kpi__label">{{ kpi.label }}</span>
          <strong class="dash-kpi__value">{{ kpi.value }}</strong>
          <span class="dash-kpi__foot">{{ kpi.foot }}</span>
        </span>
        <span class="dash-kpi__bars" aria-hidden="true"><i v-for="(bar, index) in kpi.bars" :key="index" :style="{ height: `${bar}%` }" /></span>
      </button>
    </section>

    <section class="dash-grid dash-grid--first">
      <article class="card dash-panel dash-panel--trend">
        <header class="dash-panel__head">
          <div>
            <h2 class="dash-panel__title">近{{ periodDays }}日活跃趋势</h2>
            <p class="dash-panel__sub">{{ formatDate(periodStart) }} 至 {{ formatDate(periodEnd) }} · 按用户最近活跃、注册和用药记录计算</p>
          </div>
          <div class="segmented" style="margin-left:auto">
            <button type="button" class="segmented__item" :class="{ 'segmented__item--active': trendMetric === 'active' }" @click="trendMetric = 'active'">活跃用户</button>
            <button type="button" class="segmented__item" :class="{ 'segmented__item--active': trendMetric === 'new' }" @click="trendMetric = 'new'">新增用户</button>
            <button type="button" class="segmented__item" :class="{ 'segmented__item--active': trendMetric === 'adherence' }" @click="trendMetric = 'adherence'">用药完成率</button>
          </div>
        </header>
        <div class="dash-panel__toolbar">
          <span class="dash-legend"><i class="dot dot--green" />{{ trendMetric === 'active' ? '活跃用户' : trendMetric === 'new' ? '新增注册' : '完成率（%）' }}</span>
          <div class="segmented" style="margin-left:auto">
            <button type="button" class="segmented__item" :class="{ 'segmented__item--active': rangeMode === '7' }" @click="rangeMode = '7'">近7天</button>
            <button type="button" class="segmented__item" :class="{ 'segmented__item--active': rangeMode === '30' }" @click="rangeMode = '30'">近30天</button>
            <button type="button" class="segmented__item dash-range-toggle" :class="{ 'segmented__item--active': rangeMode === 'custom' }" @click="rangeMode = 'custom'">自定义</button>
          </div>
        </div>
        <div v-if="rangeMode === 'custom'" class="dash-range">
          <ProDateRange v-model="customRange" aria-label="自定义统计周期" :placeholder="['开始日期', '结束日期']" />
          <span class="dash-range__hint">最多统计 90 天</span>
        </div>
        <EChart :option="trendOption" height="224px" :aria-label="`${formatDate(periodStart)} 至 ${formatDate(periodEnd)} 活跃趋势折线图`" />
      </article>

      <article class="card dash-panel dash-panel--donut">
        <header class="dash-panel__head">
          <div>
            <h2 class="dash-panel__title">用药提醒执行情况</h2>
            <p class="dash-panel__sub">全部用药记录状态</p>
          </div>
          <button type="button" class="card__link" @click="go('#/medication/records')">查看详情 <AppIcon name="chevron-right" :size="12" /></button>
        </header>
        <div class="dash-donut">
          <EChart :option="donutOption" height="174px" aria-label="用药记录状态环图" />
          <div class="dash-donut__legend">
            <span v-for="item in donutData" :key="item.name"><i :style="{ background: item.itemStyle.color }" />{{ item.name }}<b>{{ item.value }}</b></span>
          </div>
        </div>
        <div class="dash-donut__foot">
          <span><b>{{ adherence.toFixed(1) }}%</b> 完成率</span>
          <span class="muted">按已服药 / 全部记录</span>
        </div>
      </article>

      <article class="card dash-panel dash-panel--risk">
        <header class="dash-panel__head">
          <div>
            <h2 class="dash-panel__title">健康风险预警列表</h2>
            <p class="dash-panel__sub">待处理与处理中</p>
          </div>
          <button type="button" class="card__link" @click="go('#/health/alerts')">查看全部 <AppIcon name="chevron-right" :size="12" /></button>
        </header>
        <div class="dash-risk-list">
          <button v-for="alert in riskAlerts" :key="String(alert.id)" type="button" class="dash-risk-row" @click="openAlert(alert)">
            <span class="avatar avatar--sm" :class="`avatar--${String(alert.severity) === '高' ? 'pink' : String(alert.severity) === '中' ? 'amber' : 'blue'}`">{{ String(alert.user_name).slice(-2) }}</span>
            <span class="dash-risk-row__main">
              <span class="dash-risk-row__top"><b>{{ alert.user_name }}</b><span class="muted">{{ alert.age }} 岁</span><span class="tag" :class="`tag--${severityTone(alert.severity)}`">{{ alert.severity }}风险</span></span>
              <span class="dash-risk-row__text">{{ alert.metric_value }}，{{ alert.alert_type }}异常，待跟进</span>
            </span>
            <span class="dash-risk-row__time">{{ formatDateTime(alert.triggered_at) }}</span>
          </button>
          <div v-if="!riskAlerts.length" class="empty-state" style="min-height: 180px">
            <h3 class="empty-state__title">当前没有待处理预警</h3>
            <p class="empty-state__text">健康指标持续正常，新的异常上报会出现在这里。</p>
          </div>
        </div>
      </article>
    </section>

    <section class="dash-grid dash-grid--second">
      <article class="card dash-panel">
        <header class="dash-panel__head">
          <div>
            <h2 class="dash-panel__title">家庭守护概览</h2>
            <p class="dash-panel__sub">按家庭档案与成员结构汇总</p>
          </div>
          <button type="button" class="card__link" @click="go('#/families')">查看全部 <AppIcon name="chevron-right" :size="12" /></button>
        </header>
        <div class="dash-family">
          <div class="dash-family__kpis">
            <div><span>家庭总数</span><b>{{ formatNumber(familyOverview.total) }}</b><small><i class="dot dot--green" />正常档案</small></div>
            <div><span>家庭成员</span><b>{{ formatNumber(familyOverview.members) }}</b><small><i class="dot dot--purple" />平均 {{ familyOverview.average }} 人/户</small></div>
            <div><span>独居长者</span><b>{{ formatNumber(familyOverview.solo) }}</b><small>占比 {{ familyOverview.soloRate }}%</small></div>
          </div>
          <div class="dash-family__bars">
            <div class="dash-family__bar-row"><span>老年用户</span><div class="dash-bar"><i :style="{ width: `${percent(familyOverview.elderly, familyOverview.members)}%` }" class="dash-bar--green" /></div><b>{{ formatNumber(familyOverview.elderly) }}</b></div>
            <div class="dash-family__bar-row"><span>三代同堂</span><div class="dash-bar"><i :style="{ width: `${familyOverview.multiRate}%` }" class="dash-bar--blue" /></div><b>{{ familyOverview.multi }} 户</b></div>
            <div class="dash-family__bar-row"><span>其他成员</span><div class="dash-bar"><i :style="{ width: `${percent(familyOverview.others, familyOverview.members)}%` }" class="dash-bar--purple" /></div><b>{{ formatNumber(familyOverview.others) }}</b></div>
          </div>
        </div>
      </article>

      <article class="card dash-panel">
        <header class="dash-panel__head">
          <div>
            <h2 class="dash-panel__title">最新健康数据摘要</h2>
            <p class="dash-panel__sub">异常预警按指标类型汇总</p>
          </div>
          <button type="button" class="card__link" @click="go('#/health/monitoring')">查看详情 <AppIcon name="chevron-right" :size="12" /></button>
        </header>
        <div class="dash-metrics">
          <button v-for="metric in metricCards" :key="metric.type" type="button" class="dash-metric" :class="`dash-metric--${metric.tone}`" @click="go('#/health/monitoring')">
            <span class="dash-metric__head"><span class="dash-metric__icon"><AppIcon :name="metric.icon" :size="17" /></span>{{ metric.type }}异常 <b>{{ metric.abnormal }}</b></span>
            <span class="dash-metric__chart" aria-hidden="true"><i v-for="(h, i) in [34, 51, 41, 60, 48, 72, 58, 78, 64, 80]" :key="i" :style="{ height: `${h}%` }" /></span>
            <span class="dash-metric__foot"><span>正常 <b>{{ metric.normal }}</b></span><span>高风险 <b>{{ metric.high }}</b></span></span>
          </button>
        </div>
      </article>
    </section>

    <section class="dash-grid dash-grid--bottom">
      <article class="card dash-panel">
        <header class="dash-panel__head">
          <div>
            <h2 class="dash-panel__title">待办任务 <span class="dash-count">{{ visibleTodos.length }}</span></h2>
            <p class="dash-panel__sub">来自预警、设备、审核与消息队列</p>
          </div>
          <span class="dash-filter-tabs" role="tablist" aria-label="待办筛选">
            <button type="button" :class="{ 'is-active': todoFilter === 'all' }" :aria-selected="todoFilter === 'all'" @click="todoFilter = 'all'">全部 {{ todos.length }}</button>
            <button type="button" :class="{ 'is-active': todoFilter === 'high' }" :aria-selected="todoFilter === 'high'" @click="todoFilter = 'high'">高优先级</button>
            <button type="button" :class="{ 'is-active': todoFilter === 'today' }" :aria-selected="todoFilter === 'today'" @click="todoFilter = 'today'">今日</button>
          </span>
        </header>
        <div class="dash-todo-list">
          <button v-for="todo in visibleTodos" :key="todo.id" type="button" class="dash-todo" @click="go(todo.route)">
            <el-checkbox :model-value="completedTodos.includes(todo.id)" aria-label="标记完成" @click.stop @change="toggleTodo(todo.id)" />
            <span class="tag" :class="todo.tone === 'high' ? 'tag--danger' : todo.tone === 'warning' ? 'tag--warning' : 'tag--info'">{{ todo.tone === 'high' ? '高优先级' : todo.tone === 'warning' ? '中优先级' : '普通' }}</span>
            <span class="dash-todo__title">{{ todo.title }}</span>
            <span class="dash-todo__meta">{{ todo.meta }}</span>
            <span class="dash-todo__time">{{ formatDateTime(todo.at) }}</span>
            <AppIcon name="chevron-right" :size="13" />
          </button>
          <div v-if="!visibleTodos.length" class="empty-state" style="min-height: 170px">
            <h3 class="empty-state__title">{{ todos.length ? '当前筛选下没有待办' : '今天的待办已清空' }}</h3>
            <p class="empty-state__text">新的预警、异常设备和审核任务会自动进入待办。</p>
          </div>
        </div>
      </article>

      <article class="card dash-panel">
        <header class="dash-panel__head">
          <div>
            <h2 class="dash-panel__title">快捷操作</h2>
            <p class="dash-panel__sub">高频运营入口</p>
          </div>
        </header>
        <div class="dash-quick">
          <button v-for="item in quickActions" :key="item.label" type="button" class="dash-quick__item" @click="go(item.route)">
            <span class="dash-quick__icon" :class="`dash-quick__icon--${item.tone}`"><AppIcon :name="item.icon" :size="18" /></span>
            <span><b>{{ item.label }}</b><small>{{ item.sub }}</small></span>
            <AppIcon name="chevron-right" :size="13" />
          </button>
        </div>
        <div class="dash-guardian">
          <span class="dash-guardian__icon"><AppIcon name="heart" :size="22" /></span>
          <span><b>家庭守护计划</b><small>高风险家庭优先随访，今日还有 {{ openAlerts.filter(alert => alert.severity === '高').length }} 条高风险预警待跟进。</small></span>
          <button type="button" class="btn btn--sm" @click="go('#/health/alerts')">去跟进</button>
        </div>
      </article>
    </section>
  </div>
</template>

<style scoped>
.dash-greeting { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; margin-bottom: 16px; }
.dash-greeting h1 { font-size: 24px; line-height: 1.25; font-weight: 700; color: var(--c-ink); letter-spacing: -.2px; }
.dash-greeting p { margin-top: 5px; font-size: 12px; color: var(--c-muted); }
.dash-weather { margin-left: 8px; color: #d79213; }
.dash-divider { margin: 0 8px; color: var(--c-border-strong); }
.dash-greeting__meta { display: flex; align-items: center; gap: 10px; padding-top: 4px; }
.dash-sync { display: inline-flex; align-items: center; gap: 4px; font-size: 10px; color: var(--c-faint); }
.dash-kpis { display: grid; grid-template-columns: repeat(6, minmax(0, 1fr)); gap: 10px; margin-bottom: 14px; }
.dash-kpi {
  position: relative; overflow: hidden; min-height: 112px; display: flex; align-items: flex-start; gap: 10px;
  padding: 13px 13px 11px; text-align: left; border: 1px solid var(--c-border); border-radius: var(--radius-card);
  background: linear-gradient(145deg, #fff 0%, var(--kpi-wash, #f2fbf7) 100%); box-shadow: var(--shadow-card);
  transition: transform var(--motion-fast) var(--ease), box-shadow var(--motion-fast) var(--ease), border-color var(--motion-fast);
}
.dash-kpi:hover { transform: translateY(-1px); box-shadow: var(--shadow-raised); border-color: var(--kpi-border, var(--c-success-border)); }
.dash-kpi__icon { width: 34px; height: 34px; flex: 0 0 auto; border-radius: 10px; display: grid; place-items: center; background: var(--kpi-soft, var(--c-brand-soft)); color: var(--kpi-color, var(--c-brand-strong)); }
.dash-kpi__body { min-width: 0; display: flex; flex-direction: column; }
.dash-kpi__label { font-size: 11px; color: #53657e; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.dash-kpi__value { margin-top: 4px; font-family: var(--font-num); font-size: 26px; line-height: 1; color: var(--c-ink); font-variant-numeric: tabular-nums; letter-spacing: -.5px; }
.dash-kpi__foot { margin-top: 7px; font-size: 9.5px; color: var(--c-muted); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.dash-kpi__bars { position: absolute; right: 8px; bottom: 9px; height: 37px; width: 58px; display: flex; align-items: flex-end; gap: 2px; opacity: .28; }
.dash-kpi__bars i { flex: 1; min-width: 2px; border-radius: 2px 2px 0 0; background: var(--kpi-color, var(--c-brand)); }
.dash-kpi--brand { --kpi-color: #078e64; --kpi-soft: #e3f8ef; --kpi-wash: #f3fcf8; --kpi-border: #c8ecde; }
.dash-kpi--info { --kpi-color: #2f7dcc; --kpi-soft: #e9f3fe; --kpi-wash: #f5faff; --kpi-border: #d3e5fa; }
.dash-kpi--danger { --kpi-color: #d33a3a; --kpi-soft: #feecec; --kpi-wash: #fffafa; --kpi-border: #f6d6d6; }
.dash-kpi--purple { --kpi-color: #6e5ad2; --kpi-soft: #f0edfe; --kpi-wash: #faf9ff; --kpi-border: #e1dafb; }
.dash-kpi--cyan { --kpi-color: #0d8b9e; --kpi-soft: #e7f8fa; --kpi-wash: #f5fdfe; --kpi-border: #cfedf2; }
.dash-kpi--success { --kpi-color: #078e64; --kpi-soft: #e3f8ef; --kpi-wash: #f3fcf8; --kpi-border: #c8ecde; }
.dash-grid { display: grid; gap: 14px; margin-bottom: 14px; }
.dash-grid--first { grid-template-columns: minmax(0, 1.55fr) minmax(0, 1.02fr) minmax(0, 1.14fr); }
.dash-grid--second { grid-template-columns: minmax(0, 1.15fr) minmax(0, 1.25fr); }
.dash-grid--bottom { grid-template-columns: minmax(0, 1.4fr) minmax(0, 1fr); }
.dash-panel { min-width: 0; overflow: hidden; }
.dash-panel__head { display: flex; align-items: flex-start; gap: 10px; padding: 14px 16px 8px; }
.dash-panel__title { font-size: 14px; font-weight: 700; color: var(--c-ink); line-height: 1.35; }
.dash-panel__sub { margin-top: 2px; font-size: 9.5px; color: var(--c-faint); }
.dash-panel__toolbar { display: flex; align-items: center; padding: 0 16px 2px; gap: 8px; }
.dash-range { display: flex; align-items: center; gap: 8px; padding: 5px 16px 6px; }
.dash-range :deep(.pro-date-range) { width: 250px; max-width: 100%; }
.dash-range__hint { font-size: 9px; color: var(--c-faint); white-space: nowrap; }
.dash-legend { display: inline-flex; align-items: center; gap: 5px; font-size: 10px; color: var(--c-muted); }
.dash-panel--donut :deep(.chart-box) { margin-top: -4px; }
.dash-donut { padding: 0 10px; }
.dash-donut__legend { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 5px 12px; padding: 0 8px 10px; }
.dash-donut__legend span { display: flex; align-items: center; gap: 5px; font-size: 10px; color: var(--c-muted); }
.dash-donut__legend i { width: 6px; height: 6px; border-radius: 50%; }
.dash-donut__legend b { margin-left: auto; font-family: var(--font-num); color: var(--c-text); font-weight: 600; }
.dash-donut__foot { display: flex; align-items: center; justify-content: space-between; padding: 9px 16px; border-top: 1px solid var(--c-divider); font-size: 10px; color: var(--c-muted); }
.dash-donut__foot b { font-family: var(--font-num); font-size: 13px; color: var(--c-brand-strong); }
.dash-risk-list { padding-bottom: 4px; }
.dash-risk-row { width: 100%; display: flex; align-items: center; gap: 8px; padding: 8px 15px; text-align: left; border: 0; background: transparent; border-top: 1px solid var(--c-divider); }
.dash-risk-row:first-child { border-top: 0; }
.dash-risk-row:hover { background: #fbfefc; }
.dash-risk-row__main { min-width: 0; flex: 1; }
.dash-risk-row__top { display: flex; align-items: center; gap: 5px; font-size: 10.5px; color: var(--c-muted); }
.dash-risk-row__top b { font-size: 12px; color: var(--c-ink); }
.dash-risk-row__top .tag { margin-left: auto; height: 19px; padding: 0 6px; }
.dash-risk-row__text { display: block; margin-top: 2px; font-size: 10px; color: var(--c-muted); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.dash-risk-row__time { flex: 0 0 auto; font-size: 9px; color: var(--c-faint); align-self: flex-start; margin-top: 2px; }
.dash-family { display: grid; grid-template-columns: minmax(0, 1.15fr) minmax(0, 1fr); gap: 10px; padding: 2px 16px 15px; }
.dash-family__kpis { display: grid; gap: 6px; }
.dash-family__kpis > div { padding: 8px 10px; border-radius: 8px; background: var(--c-surface-muted); border: 1px solid var(--c-divider); }
.dash-family__kpis span { display: block; font-size: 9.5px; color: var(--c-muted); }
.dash-family__kpis b { display: block; margin-top: 2px; font-family: var(--font-num); font-size: 18px; color: var(--c-ink); line-height: 1; }
.dash-family__kpis small { display: flex; align-items: center; gap: 4px; margin-top: 4px; font-size: 8.5px; color: var(--c-muted); }
.dash-family__bars { display: flex; flex-direction: column; justify-content: center; gap: 10px; }
.dash-family__bar-row { display: grid; grid-template-columns: 58px minmax(0, 1fr) 44px; align-items: center; gap: 6px; font-size: 9.5px; color: var(--c-muted); }
.dash-family__bar-row b { font-family: var(--font-num); text-align: right; font-size: 10px; color: var(--c-text); }
.dash-bar { height: 6px; border-radius: 4px; background: #f0f4f8; overflow: hidden; }
.dash-bar i { display: block; height: 100%; border-radius: 4px; background: var(--c-brand); }
.dash-bar--blue { background: #6fa8e8 !important; }
.dash-bar--purple { background: #a68ae7 !important; }
.dash-metrics { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 8px; padding: 2px 16px 15px; }
.dash-metric { border: 1px solid var(--c-border); border-radius: 9px; background: #fff; padding: 9px; text-align: left; transition: all var(--motion-fast); }
.dash-metric:hover { border-color: var(--mc-border, var(--c-success-border)); background: var(--mc-wash, var(--c-brand-mist)); }
.dash-metric__head { display: flex; align-items: center; gap: 4px; font-size: 9.5px; color: var(--c-muted); }
.dash-metric__head b { margin-left: auto; font-family: var(--font-num); font-size: 15px; color: var(--mc, var(--c-danger)); }
.dash-metric__icon { width: 24px; height: 24px; display: grid; place-items: center; border-radius: 7px; background: var(--mc-soft, var(--c-danger-soft)); color: var(--mc, var(--c-danger)); }
.dash-metric__chart { display: flex; align-items: flex-end; gap: 2px; height: 35px; margin: 7px 0 5px; }
.dash-metric__chart i { flex: 1; background: var(--mc, var(--c-danger)); opacity: .35; border-radius: 1px 1px 0 0; }
.dash-metric__foot { display: flex; justify-content: space-between; font-size: 8.5px; color: var(--c-faint); }
.dash-metric__foot b { color: var(--c-text); font-family: var(--font-num); }
.dash-metric--danger { --mc: #e54b4b; --mc-soft: #feecec; --mc-border: #f5cccc; --mc-wash: #fffafa; }
.dash-metric--info { --mc: #2f7dcc; --mc-soft: #e9f3fe; --mc-border: #d3e5fa; --mc-wash: #f7fbff; }
.dash-metric--success { --mc: #078e64; --mc-soft: #e3f8ef; --mc-border: #c8ecde; --mc-wash: #f7fdfa; }
.dash-count { display: inline-grid; place-items: center; min-width: 19px; height: 19px; padding: 0 5px; margin-left: 3px; border-radius: 10px; background: var(--c-danger-soft); color: var(--c-danger); font-size: 10px; vertical-align: 1px; }
.dash-filter-tabs { display: flex; gap: 2px; margin-left: auto; background: var(--c-surface-muted); padding: 2px; border-radius: 7px; }
.dash-filter-tabs button { border: 0; background: transparent; border-radius: 5px; padding: 4px 7px; font-size: 9.5px; color: var(--c-muted); }
.dash-filter-tabs button.is-active { background: #fff; color: var(--c-brand-strong); font-weight: 600; box-shadow: 0 1px 2px rgba(7,26,67,.08); }
.dash-todo-list { padding: 3px 0 6px; }
.dash-todo { display: flex; align-items: center; gap: 8px; width: 100%; padding: 9px 16px; border: 0; border-top: 1px solid var(--c-divider); background: transparent; text-align: left; }
.dash-todo:first-child { border-top: 0; }
.dash-todo:hover { background: #fbfefc; }
.dash-todo .tag { height: 19px; padding: 0 6px; font-size: 9px; }
.dash-todo__title { font-size: 11px; color: var(--c-text); font-weight: 500; }
.dash-todo__meta { margin-left: auto; font-size: 9px; color: var(--c-faint); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 35%; }
.dash-todo__time { font-size: 9px; color: var(--c-faint); white-space: nowrap; }
.dash-quick { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 6px; padding: 2px 16px 10px; }
.dash-quick__item { display: flex; align-items: center; gap: 7px; padding: 8px; border: 1px solid var(--c-border); border-radius: 8px; background: #fff; text-align: left; }
.dash-quick__item:hover { border-color: var(--c-success-border); background: var(--c-brand-mist); }
.dash-quick__item > span:nth-child(2) { min-width: 0; flex: 1; display: flex; flex-direction: column; }
.dash-quick__item b { font-size: 10px; color: var(--c-text); white-space: nowrap; }
.dash-quick__item small { margin-top: 1px; font-size: 8.5px; color: var(--c-faint); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.dash-quick__icon { width: 28px; height: 28px; border-radius: 7px; display: grid; place-items: center; flex: 0 0 auto; }
.dash-quick__icon--brand { background: var(--c-brand-soft); color: var(--c-brand-strong); }
.dash-quick__icon--info { background: var(--c-info-soft); color: var(--c-info); }
.dash-quick__icon--purple { background: var(--c-purple-soft); color: var(--c-purple); }
.dash-quick__icon--warning { background: var(--c-warning-soft); color: #b87308; }
.dash-quick__icon--cyan { background: var(--c-cyan-soft); color: var(--c-cyan); }
.dash-quick__icon--neutral { background: #f2f5f8; color: #67788e; }
.dash-guardian { display: flex; align-items: center; gap: 8px; margin: 0 16px 14px; padding: 9px 10px; border-radius: 9px; background: linear-gradient(135deg, #f1fbf7, #e8f8f1); border: 1px solid #d4f0e4; }
.dash-guardian__icon { width: 32px; height: 32px; border-radius: 50%; display: grid; place-items: center; background: #fff; color: var(--c-brand-strong); flex: 0 0 auto; }
.dash-guardian > span:nth-child(2) { min-width: 0; flex: 1; display: flex; flex-direction: column; }
.dash-guardian b { font-size: 10.5px; color: #0b5f45; }
.dash-guardian small { margin-top: 1px; font-size: 8.5px; color: #4d8a77; line-height: 1.4; }
.dot { width: 6px; height: 6px; border-radius: 50%; display: inline-block; flex: 0 0 auto; }
.dot--green { background: var(--c-brand); }
.dot--blue { background: var(--c-info); }
.dot--violet { background: var(--c-purple); }
.dot--amber { background: var(--c-warning); }
.dot--red { background: var(--c-danger); }
@media (max-width: 1399px) {
  .dash-kpis { grid-template-columns: repeat(3, minmax(0, 1fr)); }
  .dash-grid--first { grid-template-columns: minmax(0, 1.45fr) minmax(0, 1fr); }
  .dash-panel--risk { grid-column: span 2; }
  .dash-risk-list { display: grid; grid-template-columns: repeat(2, minmax(0,1fr)); }
  .dash-risk-row:nth-child(2) { border-top: 0; }
}
@media (max-width: 1023px) {
  .dash-grid--first, .dash-grid--second, .dash-grid--bottom { grid-template-columns: minmax(0, 1fr); }
  .dash-panel--risk { grid-column: auto; }
  .dash-risk-list { display: block; }
  .dash-grid--second .dash-panel:last-child { grid-column: auto; }
}
@media (max-width: 767px) {
  .dash-greeting { flex-direction: column; gap: 8px; }
  .dash-greeting h1 { font-size: 21px; }
  .dash-greeting__meta { padding-top: 0; }
  .dash-kpis { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .dash-grid { gap: 12px; }
  .dash-family { grid-template-columns: minmax(0, 1fr); }
  .dash-metrics { grid-template-columns: minmax(0, 1fr); }
  .dash-quick { grid-template-columns: minmax(0, 1fr); }
  .dash-panel__head { flex-wrap: wrap; }
  .dash-panel__head .segmented { margin-left: 0 !important; }
  .dash-panel__toolbar { flex-wrap: wrap; }
  .dash-panel__toolbar .segmented { margin-left: 0 !important; }
  .dash-range { flex-direction: column; align-items: flex-start; }
  .dash-range :deep(.pro-date-range) { width: 100%; }
  .dash-todo__meta { display: none; }
}
</style>
